import { useEffect, useRef, type ComponentRef } from "react";
import { Platform } from "react-native";
import type { Href } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { ExternalLink } from "@src/components/ExternalLink";
import {
  activationCameraKey,
  chronological,
  type ActivationDetail,
  type ActivationOperatorNote,
  type ActivationSignal,
} from "@visioni/a3s-monitoring/app";
import { EMPTY, formatInstant, formatPreciseInstant, formatText } from "../format";
import { operatorNoteAnchor } from "../routes";
import { Empty, Field, InlineLink, Section } from "./DetailSection";
import { SignalProcessing } from "./SignalProcessing";
import { TimelineValue } from "./TimelineValue";

interface ActivationDetailViewProps {
  readonly activation: ActivationDetail;
  readonly tenant: { readonly label: string; readonly href?: Href };
  readonly siteLabel: (siteId: string) => string;
  readonly cameraLabel: (cameraKey: string) => string;
  readonly signalHref: (signalId: string) => Href;
  /** Omit where the camera page cannot be reached; the camera then shows unlinked. */
  readonly cameraHref?: (siteId: string, cameraKey: string) => Href;
  /** Omit where there is no tenant list to narrow; site and camera then show unlinked. */
  readonly listHref?: (filters: { site: string; camera?: string }) => Href;
  /** The URL's fragment: the operator note it names is scrolled to and marked. */
  readonly anchor?: string | undefined;
}

export function ActivationDetailView({
  activation,
  tenant,
  siteLabel,
  cameraLabel,
  signalHref,
  cameraHref,
  listHref,
  anchor,
}: ActivationDetailViewProps) {
  const cameraKey = activationCameraKey(activation);
  const site = activation.a3sSite;

  return (
    <View gap="4xl">
      <Section title="Summary">
        <Field label="Ticket">{activation.ticketNo}</Field>
        <Field label="Description">{formatText(activation.description)}</Field>
        <Field label="Pending state">{formatText(activation.pendingState)}</Field>
        <Field label="Completed status">{formatText(activation.completedStatus)}</Field>
        <Field label="Reason">{formatText(activation.reason)}</Field>
        <Field label="Priority">{activation.priority !== undefined ? String(activation.priority) : EMPTY}</Field>
        <Field label="Organisation">
          {tenant.href !== undefined ?
            <InlineLink href={tenant.href}>{tenant.label}</InlineLink>
          : tenant.label}
        </Field>
        <Field label="Site">
          {site === undefined ?
            EMPTY
          : listHref !== undefined ?
            <InlineLink href={listHref({ site })}>{siteLabel(site)}</InlineLink>
          : siteLabel(site)}
        </Field>
        <Field label="Camera">
          {site !== undefined && cameraKey !== undefined ?
            <View flexDirection="row" gap="lg" flexWrap="wrap">
              {cameraHref !== undefined ?
                <InlineLink href={cameraHref(site, cameraKey)}>{cameraLabel(cameraKey)}</InlineLink>
              : <Text textStyle="sm/regular" color="text" selectable>
                  {cameraLabel(cameraKey)}
                </Text>
              }
              {listHref !== undefined && (
                <InlineLink href={listHref({ site, camera: cameraKey })}>Other activations for this camera</InlineLink>
              )}
            </View>
          : EMPTY}
        </Field>
        {cameraKey !== undefined && <Field label="Camera key">{cameraKey}</Field>}
      </Section>

      <Section title="Timeline">
        <Field label="Created">{formatInstant(activation.createdDateTime)}</Field>
        <Field label="Responded">
          <TimelineValue
            from={activation.createdDateTime}
            at={activation.respondedDateTime}
            sinceLabel="after created"
          />
        </Field>
        <Field label="Completed">
          <TimelineValue
            from={activation.respondedDateTime ?? activation.createdDateTime}
            at={activation.completedDateTime}
            sinceLabel={activation.respondedDateTime !== undefined ? "after responded" : "after created"}
          />
        </Field>
      </Section>

      <Section title={`Signals (${String(activation.signals.length)})`}>
        {activation.signals.length === 0 ?
          <Empty>No signals on this activation.</Empty>
        : chronological(activation.signals, (s) => s.receivedDateTime).map((signal, index) => (
            <SignalRow
              key={`${signal.receivedDateTime}#${String(index)}`}
              signal={signal}
              href={signalHref(signal.signalId)}
              isFirst={index === 0}
            />
          ))
        }
      </Section>

      <Section title={`Operator notes (${String(activation.operatorNotes.length)})`}>
        {activation.operatorNotes.length === 0 ?
          <Empty>No operator notes on this activation.</Empty>
        : chronological(activation.operatorNotes, (n) => n.noteDateTime).map((note, index) => (
            <OperatorNoteRow
              key={note.noteId}
              note={note}
              isFirst={index === 0}
              isTarget={anchor === operatorNoteAnchor(note.noteId)}
            />
          ))
        }
      </Section>
    </View>
  );
}

function SignalRow({ signal, href, isFirst }: { signal: ActivationSignal; href: Href; isFirst: boolean }) {
  return (
    <View style={[styles.entry, !isFirst && styles.entryDivider]}>
      <Field label="Signal">
        <InlineLink href={href}>{signal.signalId}</InlineLink>
      </Field>
      <Field label="Received">{formatPreciseInstant(signal.receivedDateTime)}</Field>
      <Field label="Description">{formatText(signal.description)}</Field>
      <Field label="Notes">{formatText(signal.notes)}</Field>
      <Field label="a3s signal ID">{formatText(signal.a3sSignalId)}</Field>
      {/* The key, not the URL: the signed query string is noise, and the key is what AWS tooling takes. */}
      <Field label="Clip">
        {signal.a3sClip !== undefined ?
          <ExternalLink href={signal.a3sClip.url} style={styles.inlineLink}>
            <Text textStyle="sm/regular" style={styles.link}>
              {signal.a3sClip.key}
            </Text>
          </ExternalLink>
        : EMPTY}
      </Field>
      {signal.a3sProcessing !== undefined ?
        <SignalProcessing processing={signal.a3sProcessing} />
      : <Field label="a3s processing">{EMPTY}</Field>}
    </View>
  );
}

function OperatorNoteRow({
  note,
  isFirst,
  isTarget,
}: {
  note: ActivationOperatorNote;
  isFirst: boolean;
  isTarget: boolean;
}) {
  const ref = useRef<ComponentRef<typeof View>>(null);

  // The browser's own jump to the fragment runs before the activation has
  // loaded, when no note is on the page yet. Native shows the mark alone.
  useEffect(() => {
    if (isTarget && Platform.OS === "web" && ref.current) {
      (ref.current as unknown as HTMLElement).scrollIntoView({ block: "start" });
    }
  }, [isTarget]);

  return (
    <View
      ref={ref}
      nativeID={operatorNoteAnchor(note.noteId)}
      style={[styles.entry, !isFirst && styles.entryDivider, isTarget && styles.target]}
    >
      <Field label="Recorded">{formatPreciseInstant(note.noteDateTime)}</Field>
      <Field label="Type">{formatText(note.noteType)}</Field>
      <Field label="Description">{formatText(note.description)}</Field>
      {/* Free text written by a monitoring operator; line breaks are theirs. */}
      <Field label="Notes">{formatText(note.notes)}</Field>
      <Field label="Operator">{formatText(note.operatorLogin)}</Field>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  inlineLink: { alignSelf: "flex-start" },
  link: { color: theme.colors.interactive.buttons.secondary_foreground, textDecorationLine: "underline" },
  entry: { gap: theme.spacing.xs, paddingTop: theme.spacing.md },
  entryDivider: { borderTopWidth: 1, borderColor: theme.colors.borderCard },
  target: {
    paddingLeft: theme.spacing.md,
    borderLeftWidth: 3,
    borderLeftColor: theme.colors.interactive.buttons.secondary_foreground,
  },
}));
